"use client";

import { useRef, useState, type CSSProperties } from "react";
import { FacetPane, type FacetValueRow } from "./FacetPane";

/**
 * ColumnBrowser — the stack of FacetPanes ("the column browser"). Composes
 * FacetPane per pane; owns only scroll + width-drag interaction, never data
 * or persistence (`onWidthChange` hands the resolved width back to the
 * caller — see FilterShell, which persists it to `vdev_filter_columns`).
 *
 * `orientation="top"` (default): horizontal scroll row + edge arrows.
 * `orientation="side"`: vertical stack, each pane keeps its own max-height.
 *
 * RTL: the width-drag handle sits on each pane's inline-start edge and
 * dragging toward the start (left, in RTL) grows the pane — same convention
 * as FilterShell.startWidthDrag, which this generalizes.
 */
export interface ColumnBrowserPane {
  key: string;
  label: string;
  values: FacetValueRow[];
  selected: string[];
  width?: number;
  totalCount?: number;
}

export interface ColumnBrowserProps {
  panes: ColumnBrowserPane[];
  onToggle: (paneKey: string, value: string) => void;
  onClearPane?: (paneKey: string) => void;
  onWidthChange?: (paneKey: string, width: number) => void;
  orientation?: "top" | "side";
  onScrollEdges?: boolean;
}

const DEFAULT_WIDTH = 200;
const MIN_WIDTH = 80;
const MAX_WIDTH = 600;
const SCROLL_STEP = 260;

export function ColumnBrowser({
  panes,
  onToggle,
  onClearPane,
  onWidthChange,
  orientation = "top",
  onScrollEdges = true,
}: ColumnBrowserProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [dragWidth, setDragWidth] = useState<Record<string, number>>({});
  const [dragHover, setDragHover] = useState<string | null>(null);

  function scrollBy(dir: number) {
    scrollRef.current?.scrollBy({ left: dir * SCROLL_STEP, behavior: "smooth" });
  }

  function startWidthDrag(pane: ColumnBrowserPane, e: React.MouseEvent) {
    e.preventDefault();
    const startX = e.clientX;
    const startW = dragWidth[pane.key] ?? pane.width ?? DEFAULT_WIDTH;
    const move = (ev: MouseEvent) => {
      // RTL: dragging left grows the pane.
      const delta = startX - ev.clientX;
      const w = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startW + delta));
      setDragWidth((p) => ({ ...p, [pane.key]: w }));
    };
    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      setDragWidth((p) => {
        const w = p[pane.key];
        if (w && w !== pane.width) onWidthChange?.(pane.key, w);
        return p;
      });
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  }

  const containerCls = orientation === "top" ? "flex gap-2 overflow-x-auto pb-2" : "flex flex-col gap-2";
  const containerStyle: CSSProperties = orientation === "top" ? { scrollbarWidth: "thin" } : {};

  return (
    <div dir="rtl" className="relative">
      {orientation === "top" && onScrollEdges && (
        <div className="flex items-center gap-2 mb-2">
          <ScrollArrow label="›" onClick={() => scrollBy(1)} title="גלילה ימינה" />
          <ScrollArrow label="‹" onClick={() => scrollBy(-1)} title="גלילה שמאלה" />
        </div>
      )}
      <div ref={scrollRef} className={containerCls} style={containerStyle}>
        {panes.map((pane) => {
          const w = dragWidth[pane.key] ?? pane.width ?? DEFAULT_WIDTH;
          return (
            <div
              key={pane.key}
              className="shrink-0 relative"
              style={orientation === "top" ? { width: w } : undefined}
            >
              <FacetPane
                label={pane.label}
                values={pane.values}
                selected={pane.selected}
                totalCount={pane.totalCount}
                onToggle={(value) => onToggle(pane.key, value)}
                onClear={() => onClearPane?.(pane.key)}
              />
              {onWidthChange && (
                <div
                  onMouseDown={(e) => startWidthDrag(pane, e)}
                  onMouseEnter={() => setDragHover(pane.key)}
                  onMouseLeave={() => setDragHover((k) => (k === pane.key ? null : k))}
                  className="absolute top-0 bottom-0 left-0 w-1.5 cursor-col-resize"
                  style={{ background: dragHover === pane.key ? "var(--brand-teal-edge)" : "transparent" }}
                  title="גרור לשינוי רוחב"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ScrollArrow({ label, onClick, title }: { label: string; onClick: () => void; title: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="w-7 h-7 shrink-0 rounded-md flex items-center justify-center transition-colors hover:opacity-80"
      style={{ border: "1px solid var(--border-hairline)", color: "var(--fg-muted)" }}
    >
      {label}
    </button>
  );
}
